import type Address from '@modules/husleie-framework/src/types/Address.entity';
import type Country from '@modules/husleie-framework/src/types/Country.entity';
import { LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import { PrivateLandlord } from '@modules/husleie-framework/src/types/PrivateLandlord.entity';
import { ProfessionalLandlord } from '@modules/husleie-framework/src/types/ProfessionalLandlord.entity';
import UserConversionOperations from '@modules/store/user/static-classes/UserConversionOperations';
import { UserTranslationOptions } from '@modules/store/user/types/UserTranslationOptions.entity';
import { Subscription } from '@modules/design-system/components/Subscription/Subscription.entity';

interface CreateLandlordInterface {
  street: null | string;
  street2: undefined | null | string;
  postalCode: null | string;
  postalArea: undefined | null | string;
  countryCode: null | string;
  purpose: 'postal';
}

interface ApiPostalAddressInterface {
  street: null | string;
  street2?: null | string;
  postalZipcode: null | string;
  postalArea: null | string;
  purpose: 'postal';
  country: Country;
}

export default class LandlordConversionOperations {
  /**
   * Api to Vuex State conversion
   * @param apiLandlord
   * @param options
   * @returns
   */
  static apiToInternal(apiLandlord: any, options: UserTranslationOptions): PrivateLandlord | ProfessionalLandlord {
    const landlord: PrivateLandlord | ProfessionalLandlord = {
      id: null,
      type: LandlordTypeEnum.PRIVATE,
    };

    if (apiLandlord) {
      landlord.id = apiLandlord.landlordId;
      landlord.user = UserConversionOperations.apiToInternal(apiLandlord, options);

      if (apiLandlord.address) {
        const address: Address = {
          street: apiLandlord.address.street,
          additionalAddress: apiLandlord.address.street2,
          postalCode: apiLandlord.address.postalCode,
          postalArea: apiLandlord.address.postalArea,
          country: {
            code: apiLandlord.address.countryCode,
          },
        };
        landlord.address = address;
      }
    }

    return landlord;
  }

  static apiPostalAddressToStatePostalAddress(postalAddress: ApiPostalAddressInterface): Address {
    const address: Address = {
      street: postalAddress.street,
      additionalAddress: postalAddress.street2,
      postalCode: postalAddress.postalZipcode,
      postalArea: postalAddress.postalArea,
      country: postalAddress.country,
    };
    return address;
  }

  static statePostalAddressToApiPostalAddress(address: undefined | Address) {
    const response: ApiPostalAddressInterface[] = [];
    if (address) {
      const adr: ApiPostalAddressInterface = {
        street: address.street,
        street2: address.additionalAddress,
        postalZipcode: address.postalCode,
        postalArea: address.postalArea,
        purpose: 'postal',
        country: address.country,
      };
      response.push(adr);
    }

    return response;
  }

  /**
   * Vuex State to Api conversion
   * @param landlord
   * @returns
   */
  static internalToApi(landlord: PrivateLandlord | ProfessionalLandlord) {
    const landlordAPI: any = { ...UserConversionOperations.internalToApi(landlord.user) };
    if (landlord.address) {
      const landlordAddressAPI: any = {
        street: landlord.address.street,
        street2: landlord.address.additionalAddress,
        postalCode: landlord.address.postalCode,
        postalArea: landlord.address.postalArea,
        countryCode: landlord.address.country?.code,
      };
      landlordAPI.address = landlordAddressAPI;
    }

    return landlordAPI;
  }

  static createLandlordInternalToApi(landlord: PrivateLandlord | ProfessionalLandlord) {
    const landlordAPI: any = {
      name: `${landlord?.user?.firstName} ${landlord?.user?.lastName}`,
    };

    if (landlord.address) {
      const landlordAddressAPI: CreateLandlordInterface[] = [
        {
          street: landlord.address.street,
          street2: landlord.address.additionalAddress,
          postalCode: landlord.address.postalCode,
          postalArea: landlord.address.postalArea,
          countryCode: landlord.address.country?.code,
          purpose: 'postal',
        },
      ];
      landlordAPI.addresses = landlordAddressAPI;
    }

    return landlordAPI;
  }

  /**
   * Api to Vuex State conversion
   * @param apiTenancySubscription
   * @returns
   */
  static apiLandlordSubscriptionToInternal(apiTenancySubscription: any): Subscription {
    return { ...apiTenancySubscription };
  }
}
